import enDisplayNames from '../assets/bible/en/display_names.json';
import frDisplayNames from '../assets/bible/fr/display_names.json';
import esDisplayNames from '../assets/bible/es/display_names.json';
import itDisplayNames from '../assets/bible/it/display_names.json';
import deDisplayNames from '../assets/bible/de/display_names.json';
import elDisplayNames from '../assets/bible/el/display_names.json';
import { removeDiacritics } from '../utils/bible-standardizer';
import { getBookDisplayName as getBookDisplayNameFromLimits } from './bible-limits';

// Cache for the loaded display name files
const displayNamesByLocale: {[key: string]: any} = {
  en: enDisplayNames,
  fr: frDisplayNames,
  es: esDisplayNames,
  it: itDisplayNames,
  de: deDisplayNames,
  el: elDisplayNames
};

/**
 * Ensures proper capitalization of book acronyms to match the keys in display_names files
 * (First letter capitalized, rest as provided - maintaining case for multi-letter acronyms)
 */
function formatBookAcronym(acronym: string): string {
  if (!acronym || acronym.length === 0) return acronym;
  
  // Handle cases where the acronym starts with a number (like 1Co, 2Tm)
  if (/^[0-9]/.test(acronym)) {
    if (acronym.length > 1) {
      // Keep the number, capitalize the first letter after the number
      return acronym.charAt(0) + acronym.charAt(1).toUpperCase() + acronym.slice(2);
    }
    return acronym;
  }
  
  // For regular cases, capitalize the first letter
  return acronym.charAt(0).toUpperCase() + acronym.slice(1);
}

/**
 * Get a localized display name for a book acronym
 */
export function getBookDisplayName(bookAcronym: string, locale: string = 'en'): string {
  // Format the book acronym to match the capitalization in the display_names files
  const formattedAcronym = formatBookAcronym(bookAcronym);
  
  // Default to English if locale not supported
  const localeData = displayNamesByLocale[locale] || displayNamesByLocale.en;

  // First try with the formatted acronym as-is
  if (localeData[formattedAcronym]) {
    return localeData[formattedAcronym];
  }

  // If not found, try with diacritics removed
  const normalizedAcronym = removeDiacritics(formattedAcronym);

  // Find key ignoring diacritics if needed
  const matchingKey = Object.keys(localeData).find(key =>
    removeDiacritics(key).toLowerCase() === normalizedAcronym.toLowerCase()
  );

  if (matchingKey) {
    return localeData[matchingKey];
  }

  // Try other locales if not found in the requested locale
  for (const [otherLocale, otherLocaleData] of Object.entries(displayNamesByLocale)) {
    if (otherLocale !== locale) {
      if (otherLocaleData[formattedAcronym]) {
        return otherLocaleData[formattedAcronym];
      }
    }
  }

  return formattedAcronym;
}

/**
 * Format a Bible reference with localized book names
 * This function uses the translation language for book names
 * @param bookAcronym The standardized book acronym
 * @param chapter Chapter number
 * @param startVerse Optional start verse
 * @param endVerse Optional end verse
 * @param locale Language to use for display (translation language)
 */
export function formatDisplayReference(
  bookCode: string,
  chapter: string | number,
  startVerse?: number,
  endVerse?: number,
  language: string = 'en'
): string {
  // Use full display name instead of book code
  const displayName = getBookDisplayName(bookCode, language);
  
  // Format the reference with the full book name
  let formatted = `${displayName} ${chapter}`;
  
  if (startVerse !== undefined) {
    if (endVerse !== undefined && endVerse !== startVerse) {
      formatted += `:${startVerse}-${endVerse}`;
    } else {
      formatted += `:${startVerse}`;
    }
  }
  
  return formatted;
}

/**
 * Format a Bible reference for UI components such as headers and forms
 * This function uses the UI language for book names, not the translation language
 * 
 * @param bookAcronym The standardized book acronym
 * @param chapter Chapter number
 * @param startVerse Optional start verse
 * @param endVerse Optional end verse
 * @param uiLocale UI language to use for display
 */
export function formatUIReference(
  bookCode: string,
  chapter: string | number,
  startVerse?: number,
  endVerse?: number,
  language: string = 'en'
): string {
  // Get full display name for the book in the specified language
  const displayName = getBookDisplayName(bookCode, language);
  
  // Basic formatting with full book name
  let formatted = `${displayName} ${chapter}`;
  
  // Add verse info if present
  if (startVerse !== undefined) {
    if (endVerse !== undefined && endVerse !== startVerse) {
      // For verse ranges
      if (language === 'it') {
        formatted += `,${startVerse}-${endVerse}`;
      } else if (language === 'es') {
        formatted += `:${startVerse}-${endVerse}`;
      } else if (language === 'fr') {
        formatted += `,${startVerse}-${endVerse}`;
      } else {
        // Default (English)
        formatted += `:${startVerse}-${endVerse}`;
      }
    } else {
      // For single verses
      if (language === 'it' || language === 'fr') {
        formatted += `,${startVerse}`;
      } else {
        // Default (English and Spanish)
        formatted += `:${startVerse}`;
      }
    }
  }
  
  return formatted;
}

export default {
  getBookDisplayName,
  formatDisplayReference,
  formatUIReference
};
